import { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import { Check, Copy, KeyRound, Trash2 } from 'lucide-react'
import { api } from '@/services/api'
import type { ApiConnection, ApiKey, ApiUsage, CreatedApiKey } from '@/services/types'

/** A titled panel, matching the other settings tabs. */
function Panel({
  title,
  description,
  children,
}: {
  title: string
  description?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="rounded-xl border border-border bg-surface p-4 sm:p-5">
      <h2 className="text-[15px] font-bold">{title}</h2>
      {description && (
        <p className="mt-1 text-[13px] leading-relaxed text-content-muted">{description}</p>
      )}
      <div className="mt-4">{children}</div>
    </section>
  )
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true)
          window.setTimeout(() => setCopied(false), 1500)
        })
      }}
      className="flex shrink-0 items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-sunken"
      aria-label={label}
    >
      {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

function formatDate(value: string | null): string {
  if (!value) return 'Never'
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatTokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return String(value)
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.'
}

/**
 * Keys for the OpenAI-compatible API, so NevTan's models can be used from
 * NevTan Code, the NevTan AI editor extension, or any OpenAI SDK.
 */
export function ApiKeysSettings() {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [connection, setConnection] = useState<ApiConnection | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  /** Bumped when the window regains focus, so usage and last-used stay current. */
  const [refresh, setRefresh] = useState(0)

  const reload = useCallback(() => {
    setLoadError(null)
    api.listApiKeys().then(setKeys, (e) => setLoadError(errorMessage(e)))
  }, [])

  useEffect(() => {
    reload()
    api.apiConnection().then(setConnection, () => setConnection(null))
  }, [reload])

  // Keys are used from other apps, so this page goes stale while it is open.
  useEffect(() => {
    const onFocus = () => {
      reload()
      setRefresh((n) => n + 1)
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [reload])

  const active = keys.filter((key) => !key.revokedAt)
  const revoked = keys.filter((key) => key.revokedAt)

  return (
    <div className="space-y-6">
      <ConnectPanel connection={connection} />
      <CreateKeyPanel onCreated={reload} />

      <Panel title="Your keys" description="Anyone with a key can use your model quota. Revoke keys you no longer use.">
        {loadError ? (
          <p className="text-sm text-danger">{loadError}</p>
        ) : active.length === 0 ? (
          <p className="text-sm text-content-muted">No active keys yet.</p>
        ) : (
          <ul className="space-y-2">
            {active.map((key) => (
              <KeyRow key={key.id} apiKey={key} onRevoked={reload} />
            ))}
          </ul>
        )}
        {revoked.length > 0 && (
          <details className="mt-4 text-sm">
            <summary className="cursor-pointer text-content-muted">
              {revoked.length} revoked {revoked.length === 1 ? 'key' : 'keys'}
            </summary>
            <ul className="mt-2 space-y-1">
              {revoked.map((key) => (
                <li
                  key={key.id}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-2 text-content-muted"
                >
                  <span className="font-medium line-through">{key.name}</span>
                  <span className="font-mono text-xs">{key.prefix}…</span>
                  <span className="ml-auto text-xs">Revoked {formatDate(key.revokedAt)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </Panel>

      <UsagePanel refresh={refresh} />
    </div>
  )
}

function ConnectPanel({ connection }: { connection: ApiConnection | null }) {
  return (
    <Panel
      title="Connect an editor"
      description="In NevTan Code or the NevTan AI extension, choose the NevTan AI provider and paste a key. Any OpenAI-compatible tool works the same way with this base URL."
    >
      {connection ? (
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-xs font-medium text-content-muted">Base URL</p>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-raised px-3 py-2">
              <code className="min-w-0 flex-1 truncate font-mono text-sm">{connection.baseUrl}</code>
              <CopyButton value={connection.baseUrl} label="Copy base URL" />
            </div>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-content-muted">Models</p>
            {connection.models.length === 0 ? (
              <p className="text-sm text-content-muted">No models are configured on this server yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {connection.models.map((model) => (
                  <span
                    key={model}
                    className="rounded-md border border-border bg-surface-raised px-2 py-0.5 font-mono text-xs"
                    title={model === 'auto' ? 'NevTan picks the model for each request' : undefined}
                  >
                    {model}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-content-muted">Loading connection details…</p>
      )}
    </Panel>
  )
}

function CreateKeyPanel({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<CreatedApiKey | null>(null)

  const create = async () => {
    const trimmed = name.trim()
    if (!trimmed || creating) return
    setCreating(true)
    setError(null)
    try {
      setCreated(await api.createApiKey(trimmed))
      setName('')
      onCreated()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setCreating(false)
    }
  }

  return (
    <Panel title="Create a key" description="Name it after where it will be used, such as “work laptop”.">
      <div className="flex flex-wrap gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void create()
          }}
          maxLength={100}
          placeholder="Key name"
          aria-label="Key name"
          className="w-full flex-1 rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm outline-none focus:border-accent sm:w-auto"
        />
        <button
          type="button"
          onClick={() => void create()}
          disabled={!name.trim() || creating}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-content transition hover:opacity-90 disabled:opacity-60 sm:w-auto"
        >
          <KeyRound size={14} />
          {creating ? 'Creating…' : 'Create key'}
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-danger">{error}</p>}

      {created && (
        // Shown once: the server keeps only a hash, so there is no way to see
        // this key again after the panel is dismissed.
        <div className="mt-4 rounded-lg border border-accent/40 bg-accent/5 p-4 text-sm" role="status">
          <p className="font-medium">Copy your new key now. You won’t be able to see it again.</p>
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2">
            <code className="min-w-0 flex-1 select-all break-all font-mono text-xs">{created.secret}</code>
            <CopyButton value={created.secret} label="Copy API key" />
          </div>
          <button
            type="button"
            onClick={() => setCreated(null)}
            className="mt-3 text-xs text-accent hover:underline"
          >
            I’ve copied it
          </button>
        </div>
      )}
    </Panel>
  )
}

function KeyRow({ apiKey, onRevoked }: { apiKey: ApiKey; onRevoked: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const revoke = async () => {
    setError(null)
    try {
      await api.revokeApiKey(apiKey.id)
      onRevoked()
    } catch (e) {
      setError(errorMessage(e))
      setConfirming(false)
    }
  }

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-surface-raised p-3">
      <KeyRound size={16} className="shrink-0 text-content-muted" />
      <div className="min-w-[12rem] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium">{apiKey.name}</span>
          <span className="font-mono text-xs text-content-muted">{apiKey.prefix}…</span>
        </div>
        <div className="text-xs text-content-muted">
          Created {formatDate(apiKey.createdAt)} · Last used {formatDate(apiKey.lastUsedAt)}
        </div>
        {error && <p className="mt-1 text-xs text-danger">{error}</p>}
      </div>
      {confirming ? (
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-sunken"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void revoke()}
            className="rounded-md bg-danger px-2 py-1 text-xs font-medium text-white"
          >
            Revoke key
          </button>
        </div>
      ) : (
        // Two steps: an editor using this key stops working immediately.
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-danger hover:bg-danger/10"
        >
          <Trash2 size={12} /> Revoke
        </button>
      )}
    </li>
  )
}

const PERIODS = [
  { days: 1, label: 'Today' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
] as const

function UsagePanel({ refresh }: { refresh: number }) {
  const [days, setDays] = useState<number>(30)
  const [usage, setUsage] = useState<ApiUsage | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    api.apiUsage(days).then(
      (next) => {
        if (!cancelled) setUsage(next)
      },
      (e) => {
        if (!cancelled) setError(errorMessage(e))
      },
    )
    return () => {
      cancelled = true
    }
  }, [days, refresh])

  return (
    <Panel title="Usage" description="Requests and tokens across all of your keys.">
      <div className="mb-4 flex gap-1 rounded-lg border border-border bg-surface-raised p-1 sm:w-fit">
        {PERIODS.map((period) => (
          <button
            key={period.days}
            type="button"
            onClick={() => setDays(period.days)}
            aria-current={days === period.days}
            className={clsx(
              'flex-1 rounded-md px-3 py-1 text-xs font-semibold transition sm:flex-initial',
              days === period.days ? 'bg-surface text-content shadow-card' : 'text-content-muted hover:text-content',
            )}
          >
            {period.label}
          </button>
        ))}
      </div>

      {error ? (
        <p className="text-sm text-danger">{error}</p>
      ) : !usage ? (
        <p className="text-sm text-content-muted">Loading…</p>
      ) : (
        <>
          <dl className="grid grid-cols-3 gap-2">
            {[
              ['Requests', usage.requests.toLocaleString()],
              ['Input tokens', formatTokens(usage.promptTokens)],
              ['Output tokens', formatTokens(usage.completionTokens)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-border bg-surface-raised px-3 py-2">
                <dt className="text-[11px] text-content-muted">{label}</dt>
                <dd className="text-lg font-bold tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>
          {usage.models.length > 0 && (
            <table className="mt-4 w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-content-muted">
                  <th className="pb-1 font-medium">Model</th>
                  <th className="pb-1 text-right font-medium">Requests</th>
                  <th className="pb-1 text-right font-medium">Input</th>
                  <th className="pb-1 text-right font-medium">Output</th>
                </tr>
              </thead>
              <tbody>
                {usage.models.map((row) => (
                  <tr key={row.model} className="border-t border-border">
                    <td className="py-1.5 font-mono text-xs">{row.model}</td>
                    <td className="py-1.5 text-right tabular-nums">{row.requests.toLocaleString()}</td>
                    <td className="py-1.5 text-right tabular-nums">{formatTokens(row.promptTokens)}</td>
                    <td className="py-1.5 text-right tabular-nums">{formatTokens(row.completionTokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </Panel>
  )
}
